import PropTypes from "prop-types";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
// @mui
import { alpha } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
// components
import Iconify from "src/components/iconify";
//
import { FILTER_BUILDER_OPERATIONS, OPERATION_LABELS } from "../constants";
import {
  HEADER_FILTER_OPERATIONS,
  conditionToText,
  getColumnSelector,
  headerFilterValueText,
  isCondition,
  isEmptyFilter,
  isLogicalOperator,
  isNegation,
  joinWith,
  normalizeCondition,
} from "../core/filter";
import { isDateType, toDate } from "../core/format";
import HeaderFilterPopover from "./header-filter";

let nodeId = 0;

const nextId = () => {
  nodeId += 1;

  return `node${nodeId}`;
};

const createGroup = (operator = "and", items = [], negated = false) => ({
  id: nextId(),
  type: "group",
  operator,
  negated,
  items,
});

const createCondition = (field, operation, value) => ({
  id: nextId(),
  type: "condition",
  field,
  operation,
  value,
});

function toNode(expression) {
  if (isEmptyFilter(expression)) return createGroup();

  if (isCondition(expression)) {
    const [field, operation, value] = normalizeCondition(expression);

    return createCondition(field, operation, value);
  }

  if (isNegation(expression)) {
    const inner = toNode(expression[1]);

    return inner.type === "group"
      ? { ...inner, negated: !inner.negated }
      : createGroup("and", [inner], true);
  }

  let operator = "and";
  const items = [];

  expression.forEach(part => {
    if (isLogicalOperator(part)) operator = part.toLowerCase();
    else items.push(toNode(part));
  });

  return createGroup(operator, items);
}

const needsValue = operation => !["isblank", "isnotblank"].includes(operation);

const isListOperation = operation => HEADER_FILTER_OPERATIONS.includes(operation);

function fromNode(node) {
  if (node.type === "condition") {
    if (!node.field || !node.operation) return null;
    if (isListOperation(node.operation))
      return Array.isArray(node.value) && node.value.length
        ? [node.field, node.operation, node.value]
        : null;
    if (needsValue(node.operation)) {
      if (node.value == null || node.value === "") return null;
      if (Array.isArray(node.value) && node.value.every(item => item == null || item === ""))
        return null;
    }

    return needsValue(node.operation)
      ? [node.field, node.operation, node.value]
      : [node.field, node.operation, null];
  }

  const items = node.items.map(fromNode).filter(Boolean);

  if (items.length === 0) return null;

  const expression = items.length === 1 ? items[0] : joinWith(items, node.operator);

  return node.negated ? ["!", expression] : expression;
}

function typeKey(column) {
  if (!column) return "string";
  if (isDateType(column.dataType)) return "date";
  if (column.dataType === "number") return "number";
  if (column.dataType === "boolean") return "boolean";

  return "string";
}

function operationsFor(column) {
  const operations = FILTER_BUILDER_OPERATIONS[typeKey(column)];

  if (!column?.allowHeaderFiltering) return operations;
  if (!column.allowFiltering) return HEADER_FILTER_OPERATIONS;

  return [...operations, ...HEADER_FILTER_OPERATIONS];
}

function valueForOperation(node, operation) {
  if (isListOperation(operation)) return isListOperation(node.operation) ? node.value : [];
  if (operation === "between") return [null, null];

  return Array.isArray(node.value) ? null : node.value;
}

function SingleValueEditor({ column, value, onChange, locale, t, label }) {
  const kind = typeKey(column);

  if (kind === "date") {
    const date = toDate(value);

    return (
      <DatePicker
        value={date ? moment(date) : null}
        onChange={next =>
          onChange(next && moment.isMoment(next) && next.isValid() ? next.toDate() : null)
        }
        format={locale?.dateInputFormat}
        label={label}
        slotProps={{ textField: { size: "small", fullWidth: true } }}
      />
    );
  }

  if (kind === "boolean")
    return (
      <TextField
        select
        size="small"
        fullWidth
        label={label}
        value={value == null ? "" : String(value)}
        onChange={event =>
          onChange(event.target.value === "" ? null : event.target.value === "true")
        }
      >
        <MenuItem value="true">{column?.trueText ?? t("yes")}</MenuItem>
        <MenuItem value="false">{column?.falseText ?? t("no")}</MenuItem>
      </TextField>
    );

  return (
    <TextField
      size="small"
      fullWidth
      label={label}
      value={value ?? ""}
      inputProps={{ inputMode: kind === "number" ? "decimal" : "text" }}
      onChange={event => {
        const text = event.target.value;

        if (kind !== "number") {
          onChange(text);
          return;
        }

        const number = Number(text.replace(",", "."));

        onChange(text === "" ? null : Number.isNaN(number) ? text : number);
      }}
    />
  );
}

SingleValueEditor.propTypes = {
  column: PropTypes.object,
  value: PropTypes.any,
  onChange: PropTypes.func,
  locale: PropTypes.object,
  t: PropTypes.func,
  label: PropTypes.string,
};

function ListValueEditor({ column, value, onChange, ctx }) {
  const fieldRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const [open, setOpen] = useState(false);
  const values = useMemo(() => (Array.isArray(value) ? value : []), [value]);

  onChangeRef.current = onChange;

  const popoverCtx = useMemo(
    () => ({
      ...ctx.headerFilter,
      getHeaderFilterCondition: () => ({ type: "anyof", values }),
      applyHeaderFilter: (_column, _type, next) => onChangeRef.current(next),
    }),
    [ctx.headerFilter, values]
  );

  return (
    <>
      <Autocomplete
        ref={fieldRef}
        multiple
        fullWidth
        size="small"
        open={false}
        onOpen={() => setOpen(true)}
        options={[]}
        value={values}
        onChange={(event, next) => onChange(next)}
        getOptionLabel={item => headerFilterValueText(column, item, ctx.locale, ctx.t)}
        renderInput={params => (
          <TextField
            {...params}
            label={ctx.t("gridValue")}
            inputProps={{ ...params.inputProps, readOnly: true }}
          />
        )}
      />
      <HeaderFilterPopover
        target={open ? { column, anchorEl: fieldRef.current } : null}
        ctx={popoverCtx}
        onClose={() => setOpen(false)}
        keepAllSelected
      />
    </>
  );
}

ListValueEditor.propTypes = {
  column: PropTypes.object,
  value: PropTypes.any,
  onChange: PropTypes.func,
  ctx: PropTypes.object,
};

function ConditionEditor({ node, columns, ctx, onChange, onRemove }) {
  const column = columns.find(item => getColumnSelector(item) === node.field);
  const operations = operationsFor(column);
  const listOperation = isListOperation(node.operation);
  const readOnly = listOperation && !column;
  let readOnlyText = "";

  if (readOnly) {
    const { operation, value } = conditionToText(
      [node.field, node.operation, node.value],
      ctx.columnsBySelector,
      ctx.locale,
      ctx.t
    );

    readOnlyText = `${operation} ${value}`;
  }

  const setField = field => {
    const nextColumn = columns.find(item => getColumnSelector(item) === field);
    const nextOperations = operationsFor(nextColumn);

    onChange({ ...node, field, operation: nextOperations[0], value: null });
  };

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      alignItems={{ xs: "stretch", sm: "center" }}
    >
      <TextField
        select
        size="small"
        label={ctx.t("gridField")}
        value={column ? node.field : ""}
        onChange={event => setField(event.target.value)}
        sx={{ minWidth: 180, flex: "1 1 0" }}
      >
        {columns.map(item => (
          <MenuItem
            key={item.id}
            value={getColumnSelector(item)}
          >
            {item.caption}
          </MenuItem>
        ))}
      </TextField>
      {readOnly ? (
        <Typography
          variant="body2"
          sx={{ flex: "2 1 0", minWidth: 0 }}
          noWrap
          title={readOnlyText}
        >
          {readOnlyText}
        </Typography>
      ) : (
        <>
          <TextField
            select
            size="small"
            label={ctx.t("gridOperation")}
            value={operations.includes(node.operation) ? node.operation : ""}
            onChange={event =>
              onChange({
                ...node,
                operation: event.target.value,
                value: valueForOperation(node, event.target.value),
              })
            }
            sx={{ minWidth: 170, flex: "1 1 0" }}
          >
            {operations.map(operation => (
              <MenuItem
                key={operation}
                value={operation}
              >
                {ctx.t(OPERATION_LABELS[operation])}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ flex: "1.4 1 0", minWidth: 160 }}>
            {listOperation ? (
              <ListValueEditor
                column={column}
                value={node.value}
                onChange={next => onChange({ ...node, value: next })}
                ctx={ctx}
              />
            ) : null}
            {needsValue(node.operation) && node.operation === "between" ? (
              <Stack
                direction="row"
                spacing={1}
              >
                {[0, 1].map(index => (
                  <SingleValueEditor
                    key={index}
                    column={column}
                    value={Array.isArray(node.value) ? node.value[index] : null}
                    label={index === 0 ? ctx.t("from") : ctx.t("to")}
                    onChange={next =>
                      onChange({
                        ...node,
                        value: [0, 1].map(position =>
                          position === index
                            ? next
                            : Array.isArray(node.value)
                            ? node.value[position]
                            : null
                        ),
                      })
                    }
                    locale={ctx.locale}
                    t={ctx.t}
                  />
                ))}
              </Stack>
            ) : null}
            {needsValue(node.operation) && node.operation !== "between" && !listOperation ? (
              <SingleValueEditor
                column={column}
                value={node.value}
                label={ctx.t("gridValue")}
                onChange={next => onChange({ ...node, value: next })}
                locale={ctx.locale}
                t={ctx.t}
              />
            ) : null}
          </Box>
        </>
      )}
      <Tooltip title={ctx.t("gridRemoveCondition")}>
        <IconButton
          size="small"
          color="error"
          onClick={onRemove}
          sx={{ alignSelf: "center" }}
        >
          <Iconify
            icon="mingcute:close-line"
            width={18}
          />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

ConditionEditor.propTypes = {
  node: PropTypes.object,
  columns: PropTypes.array,
  ctx: PropTypes.object,
  onChange: PropTypes.func,
  onRemove: PropTypes.func,
};

function GroupEditor({ node, columns, ctx, onChange, onRemove, depth }) {
  const update = (index, next) =>
    onChange({
      ...node,
      items: node.items.map((item, position) => (position === index ? next : item)),
    });

  const remove = index =>
    onChange({ ...node, items: node.items.filter((_, position) => position !== index) });

  const firstColumn = columns[0];

  return (
    <Box
      sx={{
        pl: depth ? 2 : 0,
        borderLeft: theme =>
          depth ? `2px solid ${alpha(theme.palette.primary.main, 0.32)}` : "none",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 1.5, flexWrap: "wrap", rowGap: 1 }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={node.operator}
          onChange={(event, value) => value && onChange({ ...node, operator: value })}
        >
          <ToggleButton value="and">{ctx.t("gridAnd")}</ToggleButton>
          <ToggleButton value="or">{ctx.t("gridOr")}</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButton
          size="small"
          value="negated"
          selected={node.negated}
          onChange={() => onChange({ ...node, negated: !node.negated })}
        >
          {ctx.t("gridNot")}
        </ToggleButton>
        <Button
          size="small"
          startIcon={
            <Iconify
              icon="mingcute:add-line"
              width={16}
            />
          }
          disabled={!firstColumn}
          onClick={() =>
            onChange({
              ...node,
              items: [
                ...node.items,
                createCondition(
                  getColumnSelector(firstColumn),
                  operationsFor(firstColumn)[0],
                  null
                ),
              ],
            })
          }
        >
          {ctx.t("gridAddCondition")}
        </Button>
        <Button
          size="small"
          startIcon={
            <Iconify
              icon="mdi:format-list-group"
              width={16}
            />
          }
          onClick={() => onChange({ ...node, items: [...node.items, createGroup()] })}
        >
          {ctx.t("gridAddGroup")}
        </Button>
        {onRemove ? (
          <Tooltip title={ctx.t("gridRemoveCondition")}>
            <IconButton
              size="small"
              color="error"
              onClick={onRemove}
            >
              <Iconify
                icon="mingcute:close-line"
                width={18}
              />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
      <Stack spacing={1.5}>
        {node.items.map((item, index) =>
          item.type === "group" ? (
            <GroupEditor
              key={item.id}
              node={item}
              columns={columns}
              ctx={ctx}
              depth={depth + 1}
              onChange={next => update(index, next)}
              onRemove={() => remove(index)}
            />
          ) : (
            <ConditionEditor
              key={item.id}
              node={item}
              columns={columns}
              ctx={ctx}
              onChange={next => update(index, next)}
              onRemove={() => remove(index)}
            />
          )
        )}
        {node.items.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: "text.disabled" }}
          >
            {ctx.t("gridFilterBuilderEmpty")}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
}

GroupEditor.propTypes = {
  node: PropTypes.object,
  columns: PropTypes.array,
  ctx: PropTypes.object,
  onChange: PropTypes.func,
  onRemove: PropTypes.func,
  depth: PropTypes.number,
};

export default function FilterBuilderDialog({ open, ctx, onClose }) {
  const [root, setRoot] = useState(() => createGroup());

  useEffect(() => {
    if (!open) return;

    const node = toNode(ctx.filterValue);

    setRoot(node.type === "group" ? node : createGroup("and", [node]));
  }, [open, ctx.filterValue]);

  const columns = ctx.filterableColumns;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>{ctx.t("gridFilterBuilder")}</DialogTitle>
      <DialogContent
        dividers
        sx={{ pt: 2 }}
      >
        <GroupEditor
          node={root}
          columns={columns}
          ctx={ctx}
          depth={0}
          onChange={setRoot}
        />
      </DialogContent>
      <DialogActions>
        <Button
          color="inherit"
          onClick={onClose}
        >
          {ctx.t("cancel")}
        </Button>
        <Button
          variant="soft"
          color="primary"
          onClick={() => {
            ctx.setFilterValue(fromNode(root));
            onClose();
          }}
        >
          {ctx.t("gridApply")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

FilterBuilderDialog.propTypes = {
  open: PropTypes.bool,
  ctx: PropTypes.object,
  onClose: PropTypes.func,
};
